// Libraries
import i18next from 'i18next';

// Utilities
import { correctDecimalPrecision, getFractionalValue } from './PoolLengthUtils';
import {
    buildRepeatStepData,
    buildStepDataWithEndConditions,
    getEstimatedDuration,
    getFieldsToDisplay
} from './StepUtils';
import Personalizer from 'personalizer';
import NumberFormats from 'utils/personalizer/NumberFormats';
import Conversion from 'utils/Conversion';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import Types from 'pages/workouts/util/Types';
import { notSupportedStepTypesForMuscles } from 'utils/workout/MuscleMapUtil';
import NumberSeparators from 'utils/personalizer/NumberSeparators';

// Constants
import {
    WorkoutPageState,
    EstimateType,
    SportTypes,
    RepeatGroupDTOType,
    ExecutableStepDTOType
} from 'WorkoutPage/constants/WorkoutConstants';
import PoolWorkoutOptions from '../constants/PoolWorkoutOptions';
import DefaultWorkoutOptions from '../constants/DefaultWorkoutOptions';
import StrengthTrainingWorkoutOptions from '../constants/StrengthTrainingWorkoutOptions';
import YogaWorkoutOptions from '../constants/YogaWorkoutOptions';
import PilatesWorkoutOptions from '../constants/PilatesWorkoutOptions';
import CardioWorkoutOptions from '../constants/CardioWorkoutOptions';
import HiitWorkoutOptions from '../constants/HiitWorkoutOptions';
import RunningWorkoutOptions from '../constants/RunningWorkoutOptions';
import CyclingWorkoutOptions from '../constants/CyclingWorkoutOptions';
import MobilityWorkoutOptions from 'WorkoutPage/constants/MobilityWorkoutOptions';
import { StepFields } from '../constants/WorkoutStepConstants';

/** @typedef {import('../types/WorkoutDTO').WorkoutDTO} WorkoutDTO */
/** @typedef {import('../types/WorkoutDTO').WorkoutSegmentDTO} WorkoutSegmentDTO */
/** @typedef {import('../types/WorkoutDTO').WorkoutStepDTO} WorkoutStepDTO */
/** @typedef {import('../types/WorkoutDTO').ExecutableStepDTO} ExecutableStepDTO */

// make sure namespace(s) used in this file are loaded for translations
i18next.loadNamespaces(['units', 'workout']);

/**
 * Backbone reference: 'getFormattedDistanceString' in WorkoutUtil.js
 *
 * @param {{
 *  distance: string | number,
 *  distanceUnit: string,
 *  isSwimWorkout: boolean,
 *  isEstimate: boolean,
 *  poolLength: number | null
 *  showPoolSwim2: boolean
 * }} param
 * @returns {string}
 */
export const getFormattedDistanceString = ({
    distance,
    distanceUnit,
    isSwimWorkout,
    isEstimate,
    poolLength,
    showPoolSwim2
}) => {
    // CA-169978: cleanup poolSwim2 feature flag
    let distanceString = '';
    if (isSwimWorkout) {
        const unit =
            poolLength === null
                ? ''
                : distanceUnit === 'meter' || distanceUnit === 'kilometer'
                ? i18next.t('units:distance_unit_lap_swim_metric')
                : i18next.t('units:distance_unit_lap_swim_statute');

        // CA-169978: cleanup poolSwim2 feature flag
        let newDist = +distance;
        if (showPoolSwim2 && isEstimate) {
            // poolSwim workout estimated distance rounds to the nearest multiple of 2 x poolLength so the user ends up on the same wall they started on
            // unspecified pool size uses a default of 25, so 2 x 25 is 50
            const doublePoolLength = poolLength === null ? 50 : poolLength * 2;
            newDist = Math.round(+distance / doublePoolLength) * doublePoolLength;
            if (newDist < doublePoolLength) {
                newDist = doublePoolLength;
            }
        }

        // round pool swim est to prevent fractions from showing in estimates
        // non-estimates should not round so we can see 1/3 and 2/3 when poolLength is 33 1/3
        distanceString = isEstimate
            ? Math.round(Number(newDist)) + ' ' + unit
            : getFractionalValue(correctDecimalPrecision(String(newDist))) + ' ' + unit;
    } else {
        let distanceValue = Conversion.convertDistance(distance, 'meter', distanceUnit);
        if (isEstimate) {
            distanceValue = roundEstimatedDistance(distanceValue, distanceUnit);
        }
        distanceString = Personalizer.personalizeDistanceValue(distanceValue);
        switch (distanceUnit) {
            case 'mile':
                distanceString += ' ' + i18next.t('units:distance.mile');
                break;
            case 'kilometer':
                distanceString += ' ' + i18next.t('units:distance.kilometer');
                break;
            case 'yard':
                distanceString += ' ' + i18next.t('units:distance.yard');
                break;
            case 'meter':
                distanceString = Personalizer.personalizeDistanceValue(distanceValue, NumberFormats.NO_DECIMAL);
                distanceString += ' ' + i18next.t('units:distance.meter');
                break;
            default:
                // do nothing here
                break;
        }
    }
    return distanceString;
};

/**
 * Description: Prepend "Copy of " to the given workout name
 * Backbone Reference: copyWorkout() in backbone/pages/workouts/util/WorkoutUtil.js
 * @param {string} workoutName
 * @returns {string}
 */
export const copyWorkoutName = (workoutName) => {
    let copyWorkoutName = i18next.t('workout:workout.copy.name', {
        0: workoutName,
        interpolation: { escapeValue: false }
    });
    if (copyWorkoutName.length > 75) {
        copyWorkoutName = copyWorkoutName.substring(0, 75);
    }
    return copyWorkoutName;
};

/**
 * Description: Round estimated time based on these conditions CA-128221
 * below 10 seconds -> up to 10 seconds
 * below 20 minutes -> round up or down to nearest 10 seconds
 * above 20 minutes -> round up or down to nearest minute
 * Backbone Reference: roundEstimatedTime in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {number} time , in seconds
 * @returns {number} , in seconds
 */
export const roundEstimatedTime = (time) => {
    let estTime = time;

    if (estTime <= 10) {
        estTime = 10;
    } else if (estTime <= 20 * 60) {
        let mins = Math.floor(estTime / 60);
        let secs = Math.round(estTime % 60);
        let roundedSecs = Math.round(secs / 10) * 10;
        estTime = mins * 60 + roundedSecs;
    } else {
        let mins = Math.round(estTime / 60);
        estTime = mins * 60;
    }

    return estTime;
};

// round distance based on requirements CA-128221:
// below 0.10 miles/kilometers round up to 0.10
// above 0.10 miles/kilometers round up or down to nearest 10th of a mile/kilometer
export const roundEstimatedDistance = (distance, unitKey) => {
    let estDist = distance;

    if (['mile', 'kilometer'].includes(unitKey)) {
        if (distance <= 0.1) {
            estDist = 0.1;
        } else {
            estDist = Math.round(distance * 10) / 10;
        }
    }

    return estDist;
};

/**
 * push Google Analytics events for workout actions
 * @param {string} action
 * @param {string} [label]
 */
export const pushWorkoutEventToAnalytics = (action, label) => {
    const analyticsObject = {
        event: 'track_event',
        category: 'Workout Page Events',
        action
    };
    AnalyticsUtil.push(label ? { ...analyticsObject, label } : analyticsObject);
};

/**
 * Backbone Reference: sendAnalytics in WorkoutPageView.js
 * push Google Analytics event when saving workouts with Power Zone target
 * @param {WorkoutDTO} workoutData
 */
export const pushAnalyticsForPowerZone = (workoutData) => {
    /**
     * Internal recursive function to check if any step has power zone target
     * @param {WorkoutStepDTO} step
     * @returns {boolean}
     */
    const stepHasPowerZoneTarget = (step) => {
        if (step.type === RepeatGroupDTOType) {
            return step?.workoutSteps?.some((step) => stepHasPowerZoneTarget(step)) ?? false;
        } else {
            return step.targetType?.workoutTargetTypeKey === 'power.zone';
        }
    };

    const workoutSteps = workoutData?.workoutSegments.flatMap((segment) => segment.workoutSteps) ?? [];

    if (workoutSteps.some((step) => stepHasPowerZoneTarget(step))) {
        pushWorkoutEventToAnalytics('Save Workout', `Includes ${workoutData.sportType.sportTypeKey} Power Target`);
    }
};

/**
 * Description: Should components show 'edit' state in UI, multiple page states need to show edit UI
 * @param {string} pageState
 * @returns {boolean}
 */
export const showEditMode = (pageState) => {
    return [WorkoutPageState.Create, WorkoutPageState.Edit, WorkoutPageState.Saved].includes(pageState);
};

/** @typedef {import('../types/WorkoutOptionsConfig').WorkoutOptionsConfig} WorkoutOptionsConfig */

/**
 * Backbone Reference: getNewWorkoutConfiguration in backbone/pages/workouts/WorkoutPageView.js
 * TODO: add configuration for other workout
 * @param {string} [sportTypeKey]
 * @returns {WorkoutOptionsConfig}
 */
export const getWorkoutConfiguration = (sportTypeKey) => {
    let configuration = null;
    switch (sportTypeKey) {
        case Types.SportTypes.Running:
            configuration = RunningWorkoutOptions;
            break;
        case Types.SportTypes.Cycling:
            configuration = CyclingWorkoutOptions;
            break;
        case Types.SportTypes.Swimming:
            configuration = PoolWorkoutOptions;
            break;
        case Types.SportTypes.Strength_Training:
            configuration = StrengthTrainingWorkoutOptions;
            break;
        case Types.SportTypes.Hiit:
            configuration = HiitWorkoutOptions;
            break;
        case Types.SportTypes.Yoga:
            configuration = YogaWorkoutOptions;
            break;
        case Types.SportTypes.Pilates:
            configuration = PilatesWorkoutOptions;
            break;
        case Types.SportTypes.Mobility:
            configuration = MobilityWorkoutOptions;
            break;
        case Types.SportTypes.Cardio:
            configuration = CardioWorkoutOptions;
            break;
        case Types.SportTypes.Other:
        default:
            configuration = DefaultWorkoutOptions;
            break;
    }
    return configuration;
};

/**
 * Calculate estimate time/distance/distance unit from workout steps
 * Backbone Reference: getOverallEstimates() in backbone/pages/workouts/WorkoutPageView.js
 * @param {WorkoutDTO} workoutData
 * @param {WorkoutStepDTO[]} workoutSteps
 * @param {boolean} isMetric
 * @param {boolean} showPoolSwim2 // CA-169978: remove showPoolSwim2 once the feature is released and works well
 * @param {boolean} isInRepeatStep
 * @returns {object}
 */
export const getOverallEstimates = (
    workoutData,
    workoutSteps,
    isMetric,
    showPoolSwim2,
    isInRepeatStep = false,
    seenPoolNonRestLapBtnPress = false
) => {
    // CA-169978: Clean up poolSwim2
    const isSwimWorkout = workoutData.sportType.sportTypeKey === Types.SportTypes.Swimming;
    let estimateType = null;
    let time = 0;
    let dist = 0;
    let distanceUnit = null;
    const userPreferenceUnits = isMetric ? 'kilometer' : 'mile';

    let hasEstimate = true;
    let seenMeters = false;
    let seenKilometers = false;
    let seenMiles = false;

    for (const step of workoutSteps) {
        const stepType = step.stepType.stepTypeKey;
        const stepDurationType = step.endCondition ? step.endCondition.conditionTypeKey : null;
        let stepUnit = userPreferenceUnits;

        if (step.type === RepeatGroupDTOType) {
            const childSteps = step.workoutSteps;

            if (step.endCondition?.conditionTypeKey === Types.ConditionTypes.Time) {
                time += step.endConditionValue ?? 0;
            } else {
                // CA-169978: remove showPoolSwim2 once the feature is released and works well
                const childStepsResult = getOverallEstimates(
                    workoutData,
                    childSteps,
                    isMetric,
                    showPoolSwim2,
                    true,
                    seenPoolNonRestLapBtnPress
                );
                estimateType = childStepsResult.estimateType;
                distanceUnit = childStepsResult.distanceUnit;
                hasEstimate = childStepsResult.hasEstimate;
                seenMeters = childStepsResult.seenMeters;
                seenKilometers = childStepsResult.seenKilometers;
                seenMiles = childStepsResult.seenMiles;
                seenPoolNonRestLapBtnPress = childStepsResult.seenPoolNonRestLapBtnPress;

                time += childStepsResult.time * (step.numberOfIterations ?? 0);
                dist += childStepsResult.dist * (step.numberOfIterations ?? 0);

                const lastStep = childSteps.at(-1);
                if (
                    step.skipLastRestStep &&
                    lastStep &&
                    (lastStep.stepType.stepTypeKey === Types.StepTypes.Recovery ||
                        lastStep.stepType.stepTypeKey === Types.StepTypes.Rest)
                ) {
                    const lastStepUnit = lastStep.preferredEndConditionUnit?.unitKey || userPreferenceUnits;
                    const { estNumber } = getEstimatedDuration(
                        lastStep,
                        workoutData.sportType.sportTypeKey ?? '',
                        workoutData.avgTrainingSpeed ?? 0,
                        lastStepUnit,
                        null,
                        showPoolSwim2 // CA-169978: cleanup poolSwim2 feature flag
                    );
                    if (lastStep.endCondition?.conditionTypeKey === Types.ConditionTypes.Distance) {
                        time -= estNumber;
                        dist -= lastStep.endConditionValue ?? 0;
                    } else if (lastStep.endCondition?.conditionTypeKey === Types.ConditionTypes.Time) {
                        time -= lastStep.endConditionValue ?? 0;
                        dist -= estNumber;
                    }
                }
            }
        } else {
            // pool swim rest steps are exempt from estimates rules
            if (isSwimWorkout) {
                if (stepType !== Types.StepTypes.Rest) {
                    if (step.endCondition?.conditionTypeKey === Types.ConditionTypes.Lap_Button) {
                        seenPoolNonRestLapBtnPress = true;
                    }
                    estimateType = updateEstimateType(estimateType, stepDurationType);
                }
            } else {
                estimateType = updateEstimateType(estimateType, stepDurationType);
            }

            // CA-169978: Clean up poolSwim2
            if (estimateType === null) {
                hasEstimate = false;
                continue;
            }

            if (step.preferredEndConditionUnit?.unitKey) {
                stepUnit = step.preferredEndConditionUnit.unitKey;
            }

            if (distanceUnit === null) {
                distanceUnit = stepUnit;
            } else if (distanceUnit !== stepUnit) {
                distanceUnit = 'mixed';
            }

            if (stepUnit === 'meter') {
                seenMeters = true;
            } else if (stepUnit === 'kilometer') {
                seenKilometers = true;
            } else if (stepUnit === 'mile') {
                seenMiles = true;
            }

            const { estNumber } = getEstimatedDuration(
                step,
                workoutData.sportType.sportTypeKey ?? '',
                workoutData.avgTrainingSpeed ?? 0,
                stepUnit,
                null,
                showPoolSwim2 // CA-169978: cleanup poolSwim2 feature flag
            );
            if (step.endCondition?.conditionTypeKey === Types.ConditionTypes.Distance) {
                time += estNumber;
                dist += step.endConditionValue ?? 0;
            } else if (step.endCondition?.conditionTypeKey === Types.ConditionTypes.Time) {
                time += step.endConditionValue ?? 0;
                dist += estNumber;
            }
        }
    }

    // pool swim doesn't have total/est distance if any non-rest step is lap button press
    if (seenPoolNonRestLapBtnPress) {
        dist = 0;
    }

    if (!hasEstimate) {
        estimateType = null;
        time = 0;
        // pool swim workout shows total distance even if there are lap button steps
        if (!isSwimWorkout) {
            dist = 0;
        }
    }

    if (distanceUnit === 'mixed' && !isInRepeatStep) {
        // if the distance unit is mixed, default to user's preference settings, and push up null for the estimate type
        if (seenKilometers && seenMeters && !seenMiles && !isSwimWorkout) {
            distanceUnit = 'meter';
        } else {
            distanceUnit = userPreferenceUnits;
        }
    }

    if (isSwimWorkout) {
        distanceUnit = null;
        // CA-169978: remove showPoolSwim2 check and keep the logic once feature is released
        if (showPoolSwim2) {
            // pool swim doesn't support time estimates so make sure time is set to 0
            if (estimateType === 'TIME_ESTIMATED') {
                time = 0;
            } else if (estimateType === 'TIME_AND_DISTANCE_ESTIMATED') {
                // pool swim doesn't suppor time estimates so remove it but keep distance estimate
                estimateType = 'DISTANCE_ESTIMATED';
                time = 0;
            }
        }
    }

    /**
     * sports that don't have training speed don't have estimate,
     * but still use this field to display total time/total distance.
     * custom workout can display either time or distance, but if both exist then
     * none of them count.
     */
    if (!requireTrainingSpeed(workoutData.sportType.sportTypeKey)) {
        if (estimateType === EstimateType.TimeAndDistance) {
            time = 0;
            dist = 0;
        }
        estimateType = null;
    }

    return {
        estimateType,
        time,
        dist,
        distanceUnit,
        hasEstimate,
        seenMeters,
        seenKilometers,
        seenMiles,
        seenPoolNonRestLapBtnPress
    };
};

/**
 * Update overall estimate type using duration type of next step
 * Backbone Reference: getWorkoutStepType() in in backbone/pages/workouts/WoroutPageView.js
 * @param {string} estimateType
 * @param {string | null} [stepDurationType]
 * @returns {string | null}
 */
const updateEstimateType = (estimateType, stepDurationType) => {
    const isTimeBased = stepDurationType === Types.DurationTypes.Time;
    const isDistanceBased = stepDurationType === Types.DurationTypes.Distance;

    if (!(isTimeBased || isDistanceBased)) {
        // don't estimate non time/distance durations
        return null;
    } else if (estimateType === null) {
        if (isTimeBased) {
            return EstimateType.Distance;
        } else {
            return EstimateType.Time;
        }
    } else {
        if (
            (estimateType === EstimateType.Distance && isDistanceBased) ||
            (estimateType === EstimateType.Time && isTimeBased)
        ) {
            return EstimateType.TimeAndDistance;
        }
    }
    return estimateType;
};

export const RequireTrainingSpeedSportTypes = [
    Types.SportTypes.Running,
    Types.SportTypes.Cycling,
    Types.SportTypes.Swimming
];

export const requireTrainingSpeed = (sportTypeKey) =>
    RequireTrainingSpeedSportTypes.some((type) => type === sportTypeKey);

/**
 * Backbone Reference: addDefaultSteps() in backbone/pages/workouts/WoroutPageView.js
 * @param {string} workoutName
 * @param {object} userAvgPace
 * @param {object} viewerSocialProfile
 * @param {string} sportTypeKey
 * @param {string | number} poolLength
 * @param {string} poolLengthUnitKey
 * @param {boolean} showPoolSwim2
 * @returns {object}
 */
export const buildWorkoutData = (
    workoutName,
    userAvgPace,
    viewerSocialProfile,
    sportTypeKey,
    poolLength,
    poolLengthUnitKey,
    showPoolSwim2 // CA-169978: Clean up poolSwim2
) => {
    /** @type {WorkoutDTO} */
    const workoutData = {
        sportType: { sportTypeKey },
        subSportType: null,
        workoutName: workoutName,
        estimatedDistanceUnit: {},
        workoutSegments: []
    };

    if (requireTrainingSpeed(sportTypeKey)) {
        // CA-169978: cleanup poolSwim2 feature flag
        workoutData.avgTrainingSpeed = getAvgTrainingSpeed(
            userAvgPace,
            viewerSocialProfile,
            sportTypeKey,
            showPoolSwim2
        );
    }

    // CA-169978: Clean up poolSwim2
    const workoutSteps = addDefaultSteps(sportTypeKey, poolLengthUnitKey, showPoolSwim2);
    const stepId = setStepId(workoutSteps);
    workoutData.workoutSegments.push({
        segmentOrder: 1,
        sportType: { sportTypeKey },
        workoutSteps: workoutSteps
    });

    if (sportTypeKey === Types.SportTypes.Swimming) {
        workoutData.poolLength = Number(poolLength);
        workoutData.poolLengthUnit = { unitKey: poolLengthUnitKey };
    }

    return {
        newWorkoutData: workoutData,
        stepId
    };
};

/**
 * Backbone Reference: newAddDefaultSteps() in backbone/pages/workouts/WoroutPageView.js
 * @param {string} sportTypeKey
 * @param {string} poolLengthUnitKey
 * @param {boolean} showPoolSwim2 // CA-169978: Clean up poolSwim2
 * @returns {object[]}
 */
// CA-169978: Clean up poolSwim2
const addDefaultSteps = (sportTypeKey, poolLengthUnitKey, showPoolSwim2) => {
    const workoutSteps = [];
    switch (sportTypeKey) {
        case Types.SportTypes.Swimming:
            addPoolSwimDefaults(workoutSteps, sportTypeKey, poolLengthUnitKey, showPoolSwim2); // CA-169978: Clean up poolSwim2
            break;
        case Types.SportTypes.Strength_Training:
            /**
             * Warm Up - Cardio Exercise - Lap Button Press, where the cardio exercise is added in StepUtils.js - buildStepData()
             */
            workoutSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Warm_Up,
                    Types.DurationTypes.Lap_Button
                )
            );

            /**
             * Repeat 3 Sets
             *   ├── Exercise - 10 Reps
             *   └── Rest     - Lap Button Press
             */
            const strengthInnerSteps = [];
            strengthInnerSteps.push(buildStepDataWithEndConditions(sportTypeKey, poolLengthUnitKey));
            strengthInnerSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Rest,
                    Types.DurationTypes.Lap_Button
                )
            );
            const strengthRepeatStep = buildRepeatStepData(strengthInnerSteps, sportTypeKey);
            workoutSteps.push(strengthRepeatStep);
            break;
        case Types.SportTypes.Hiit:
            // Warm Up
            workoutSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Warm_Up,
                    Types.DurationTypes.Lap_Button
                )
            );

            // Repeat
            const hiitInnerSteps = [];
            hiitInnerSteps.push(buildStepDataWithEndConditions(sportTypeKey, poolLengthUnitKey));
            hiitInnerSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Rest,
                    Types.DurationTypes.Time
                )
            );
            const hiitRepeatStep = buildRepeatStepData(hiitInnerSteps, sportTypeKey);
            workoutSteps.push(hiitRepeatStep);

            // Cool Down
            workoutSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Cool_Down,
                    Types.DurationTypes.Lap_Button
                )
            );
            break;
        case Types.SportTypes.Cycling:
        case Types.SportTypes.Running:
        case Types.SportTypes.Yoga:
        case Types.SportTypes.Cardio:
        case Types.SportTypes.Pilates:
        case Types.SportTypes.Mobility:
        default:
            workoutSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Warm_Up,
                    Types.DurationTypes.Lap_Button
                )
            );
            workoutSteps.push(buildStepDataWithEndConditions(sportTypeKey, poolLengthUnitKey));
            workoutSteps.push(
                buildStepDataWithEndConditions(
                    sportTypeKey,
                    poolLengthUnitKey,
                    Types.StepTypes.Cool_Down,
                    Types.DurationTypes.Lap_Button
                )
            );
            break;
    }
    return workoutSteps;
};

// CA-169978: Clean up poolSwim2
const addPoolSwimDefaults = (workoutSteps, sportTypeKey, poolLengthUnitKey, showPoolSwim2) => {
    // CA-169978: Clean up poolSwim2
    if (showPoolSwim2) {
        // Warm Up
        workoutSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Warm_Up,
                Types.DurationTypes.Distance,
                {
                    endConditionValue: 400,
                    strokeType: { strokeTypeKey: Types.StrokeTypes.Free }
                }
            )
        );

        // Rest
        workoutSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Rest,
                Types.DurationTypes.Lap_Button
            )
        );

        // Repeat
        const swimInnerSteps = [];
        // Repeat -> Swim
        swimInnerSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Main,
                Types.DurationTypes.Distance,
                {
                    endConditionValue: 100,
                    strokeType: { strokeTypeKey: Types.StrokeTypes.Free }
                }
            )
        );
        // Repeat -> Rest
        swimInnerSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Rest,
                Types.DurationTypes.Fixed_Rest,
                {
                    endConditionValue: 15
                }
            )
        );
        const swimRepeatStep = buildRepeatStepData(swimInnerSteps, sportTypeKey, { numberOfIterations: 8 });
        workoutSteps.push(swimRepeatStep);

        // Rest
        workoutSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Rest,
                Types.DurationTypes.Lap_Button
            )
        );

        // Cool Down
        workoutSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Cool_Down,
                Types.DurationTypes.Distance,
                {
                    endConditionValue: 200,
                    strokeType: { strokeTypeKey: Types.StrokeTypes.Any },
                    secondaryTargetType: { workoutTargetTypeKey: Types.TargetTypes.Effort_Based },
                    secondaryTargetValueOne: 3, // Easy
                    secondaryTargetValueTwo: 0
                }
            )
        );
    } else {
        // Warm Up
        workoutSteps.push(buildStepDataWithEndConditions(sportTypeKey, poolLengthUnitKey, Types.StepTypes.Warm_Up));

        // Rest
        workoutSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Rest,
                Types.DurationTypes.Lap_Button
            )
        );

        // Repeat
        const swimInnerSteps = [];
        // Repeat -> Swim
        swimInnerSteps.push(buildStepDataWithEndConditions(sportTypeKey, poolLengthUnitKey));
        // Repeat -> Rest
        swimInnerSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Rest,
                Types.DurationTypes.Lap_Button
            )
        );
        const swimRepeatStep = buildRepeatStepData(swimInnerSteps, sportTypeKey);
        workoutSteps.push(swimRepeatStep);

        // Rest
        workoutSteps.push(
            buildStepDataWithEndConditions(
                sportTypeKey,
                poolLengthUnitKey,
                Types.StepTypes.Rest,
                Types.DurationTypes.Lap_Button
            )
        );

        // Cool Down
        workoutSteps.push(buildStepDataWithEndConditions(sportTypeKey, poolLengthUnitKey, Types.StepTypes.Cool_Down));
    }
};

const GLOBAL_PACES = {
    runningDefault: 2.5948890522457804,
    cyclingDefault: 5.886546465213485,
    poolSwimmingDefault: 0.6314947347417719
};

/**
 * Backbone Reference: getGlobalPace() in backbone/pages/workouts/util/WoroutUtil.js
 * @param {string} sportTypeKey
 * @returns {number}
 */
const getGlobalPace = (sportTypeKey) => {
    let preferenceKey = '';
    if (sportTypeKey === Types.SportTypes.Running) {
        preferenceKey = 'runningDefault';
    } else if (sportTypeKey === Types.SportTypes.Cycling) {
        preferenceKey = 'cyclingDefault';
    } else {
        preferenceKey = 'poolSwimmingDefault';
    }
    return GLOBAL_PACES[preferenceKey];
};

/**
 * Backbone Reference: getPace() in backbone/pages/workouts/WorkoutPageView.js
 * @param {{[activityType: string]: {avgSpeed: number}}} userAvgPace
 * @param {{[trainingSpeed: string]: number}} viewerSocialProfile
 * @param {string} sportTypeKey
 * @param {boolean} showPoolSwim2
 * @returns {number}
 */
export const getAvgTrainingSpeed = (userAvgPace, viewerSocialProfile, sportTypeKey, showPoolSwim2) => {
    const userAverageTrainingSpeed = userAvgPace[sportTypeKey]?.avgSpeed;
    const socialProfileTrainingSpeed = viewerSocialProfile[`${sportTypeKey}TrainingSpeed`];

    // CA-169978: cleanup feature flag
    // poolSwim workouts uses a pace of 2:00 / 100meters for all users regardless of other factors
    if (showPoolSwim2 && sportTypeKey === Types.SportTypes.Swimming) {
        return 0.8333; // 2:00 / 100meters
    }
    if (userAverageTrainingSpeed) {
        return userAverageTrainingSpeed;
    } else if (socialProfileTrainingSpeed) {
        return socialProfileTrainingSpeed;
    }
    return getGlobalPace(sportTypeKey);
};

/**
 *
 * @param {object[]} workoutSteps
 * @param {number} currStepId
 * @returns {number}
 */
export const setStepId = (workoutSteps, currStepId = 1) => {
    workoutSteps.forEach((workoutstep) => {
        if (workoutstep.stepType.stepTypeKey === Types.StepTypes.Repeat) {
            currStepId = setStepId(workoutstep.workoutSteps, currStepId);
        }
        workoutstep.stepId = currStepId++;
    });
    return currStepId;
};

/**
 * Backbone Reference: getDefaultWorkoutName() in backbone/pages/workouts/util/WoroutUtil.js
 * @param {string} sportType
 * @param {boolean} isWheelchairMode
 * @returns {string}
 */
export const getDefaultWorkoutName = (sportType, isWheelchairMode = false) => {
    const translateKey = `workout:workout.default.title.${sportType}`;
    if (!i18next.exists(translateKey)) {
        return '';
    }
    let defaultWorkoutName = i18next.t(translateKey);
    if (isWheelchairMode) {
        if (sportType === Types.SportTypes.Running) {
            defaultWorkoutName = i18next.t('workout:workout.default.title.push.run.speed');
        }
        if (sportType === Types.SportTypes.Cycling) {
            defaultWorkoutName = i18next.t('workout:workout.default.title.handcycling');
        }
    }
    return defaultWorkoutName;
};

/**
 *
 * @param {object[]} workoutSteps
 * @param {string} unitKey
 * @returns {object[]}
 */
export const updateEndConditionUnit = (workoutSteps, unitKey) => {
    return workoutSteps.map((workoutStep) => {
        if (workoutStep.stepType.stepTypeKey === Types.StepTypes.Repeat) {
            workoutStep.workoutSteps = updateEndConditionUnit(workoutStep.workoutSteps, unitKey);
        } else {
            workoutStep.preferredEndConditionUnit = { unitKey };
        }
        return workoutStep;
    });
};

/**
 * Description: Return the step data for the given stepId
 * @param {number} stepId
 * @param {{workoutSteps: WorkoutStepDTO[]}} parent // could be either a segment or a repeatStep, must have workoutSteps as a field in the obj
 * @returns {object}
 */
export const getStepDataByStepId = (stepId, parent) => {
    let foundStep = {};

    const findStepRecursively = (steps) => {
        steps.map((step) => {
            if (step.stepId === +stepId) {
                foundStep = step;
            }
            if (step.type === RepeatGroupDTOType) {
                findStepRecursively(step.workoutSteps);
            }
        });
    };

    findStepRecursively(parent.workoutSteps);

    return foundStep;
};

/**
 * Used to format time input during editing
 * e.g. 01:111 -> 01:11:1
 *      01:123:00 -> 01:23:00
 * this function doesn't do carry like: 70:00 -> 01:10:00
 * @param {string} oldTime
 * @param {string} newTime
 * @param {number} maxNumberOfUnits 3: hh:mm:ss, 2: mm:ss
 * @returns
 */
export const formatTimeInput = (oldTime, newTime, maxNumberOfUnits) => {
    // remove invalid character and consecutive colon
    newTime = newTime.replace(/([^0-9:])/g, '');
    newTime = newTime.replace(/::/g, ':');

    const splittedTime = newTime.split(':');
    // prevent manually input too many colon
    if (splittedTime.length > maxNumberOfUnits) {
        return oldTime;
    }
    let lastUnit = splittedTime.pop();
    if (!lastUnit) {
        return newTime;
    }
    // split last unit if its digits more than 2
    if (splittedTime.length + 1 < maxNumberOfUnits && lastUnit.length > 2) {
        lastUnit = lastUnit.slice(0, -1) + ':' + lastUnit.slice(-1);
    }
    // remove largest digit for each time unit
    // and return first <maxNumberOfUnits> units
    return splittedTime
        .concat(lastUnit.split(':'))
        .map((unit) => unit.slice(-2))
        .slice(0, maxNumberOfUnits)
        .join(':');
};

/**
 *
 * @param {string} value
 * @param {number} precision
 * @returns
 */
export const formatNumberInputSeparator = (value, precision) => {
    const isPeriodSeparator = Personalizer.getNumberSeparator() === NumberSeparators.DECIMAL_PERIOD;
    const numericValue = isPeriodSeparator
        ? value.replace(/[^0-9.]/g, '').replace(/\.\./g, '.')
        : value.replace(/[^0-9,]/g, '').replace(/\,\,/g, ',');
    const separator = isPeriodSeparator ? '.' : ',';

    const decimalIndex = numericValue.indexOf(separator);
    if (decimalIndex !== -1) {
        const integerPart = numericValue.slice(0, decimalIndex);
        const decimalPart = numericValue.slice(decimalIndex + 1);
        return integerPart + separator + decimalPart.slice(0, precision);
    }
    return numericValue;
};

/**
 * Description: Determine if a step is inside of a repeat anywhere in the workout segments
 * @param {Array} segments
 * @param {string} stepId
 * @returns {boolean}
 */
export const isStepInRepeat = (segments, stepId) => {
    let inRepeat = false;
    let numDeep = 0;

    const findStepRecursively = (steps) => {
        for (const step of steps) {
            if (step.stepId === stepId) {
                inRepeat = numDeep > 0;
                return;
            }
            if (step.workoutSteps && step.workoutSteps?.length > 0) {
                numDeep += 1;
                findStepRecursively(step.workoutSteps);
            }
        }
        numDeep -= 1;
    };

    for (const segment of segments) {
        findStepRecursively(segment.workoutSteps);
    }

    return inRepeat;
};

/**
 * Util function to extract exercises from workout segments
 * Reference: extractExercises function from src/utils/workout/WorkoutUtil.js
 * @param {WorkoutSegmentDTO} workoutSegment
 * @returns {{exerciseKey: string; categoryKey: string}[]}
 */
export const extractWorkoutExercises = (workoutSegment) => {
    /**
     * @type {{exerciseKey: string; categoryKey: string}[]}
     */
    const exercises = [];

    /**
     * Internal recursive function to build exercise key arrays for workout steps
     * @param {WorkoutStepDTO[]} workoutSteps
     */
    const extractStepsExercises = (workoutSteps) => {
        if (workoutSteps.length === 0) return;

        workoutSteps.forEach((step) => {
            const { stepType, type } = step;

            if (type === RepeatGroupDTOType) {
                extractStepsExercises(step.workoutSteps || []);
            } else {
                const { exerciseName, category } = step;
                if (
                    stepType.stepTypeKey &&
                    !notSupportedStepTypesForMuscles.includes(stepType.stepTypeKey) &&
                    (exerciseName || category) &&
                    category
                ) {
                    exercises.push({ exerciseKey: exerciseName || category, categoryKey: category });
                }
            }
        });
    };

    if (workoutSegment?.workoutSteps?.length) {
        extractStepsExercises(workoutSegment.workoutSteps);
    }

    return exercises;
};

/**
 * Util function to check if specific workoutType has muscle map support
 * @param {string} workoutType
 * @returns {boolean}
 */
export const isMuscleMapSupport = (workoutType) => {
    return [
        Types.SportTypes.Strength_Training,
        Types.SportTypes.Cardio,
        Types.SportTypes.Hiit,
        Types.SportTypes.Pilates,
        Types.SportTypes.Mobility
    ].includes(workoutType);
};

/**
 * Util function to check if specific workoutType has exercise/pose support
 * @param {string} workoutType
 * @returns {boolean}
 */
export const isExerciseSupport = (workoutType) => {
    return [
        Types.SportTypes.Strength_Training,
        Types.SportTypes.Cardio,
        Types.SportTypes.Hiit,
        Types.SportTypes.Pilates,
        Types.SportTypes.Yoga,
        Types.SportTypes.Mobility
    ].includes(workoutType);
};

/**
 * Util function to check if specific workoutType has weight support
 * @param {string} workoutType
 * @returns {boolean}
 */
export const isWeightSupport = (workoutType) => {
    return [Types.SportTypes.Strength_Training, Types.SportTypes.Cardio, Types.SportTypes.Hiit].includes(workoutType);
};

/**
 * Description: Return true if any workout step matches the criteria in searchFunc
 * @param {Array} steps
 * @param {function} searchFunc
 * @param {boolean | undefined} found // only passed in for recursion
 * @returns {boolean}
 */
export const searchStepsRecursively = (steps, searchFunc, found = false) => {
    if (!found) {
        steps.map((step) => {
            if (!found) {
                // recurse through repeat blocks
                if (step.workoutSteps?.length > 0) {
                    found = searchFunc(step) || searchStepsRecursively(step.workoutSteps, searchFunc, found);
                } else {
                    found = searchFunc(step);
                }
            }
        });
    }
    return found;
};

/**
 * Util function to update all step exerciseName, the main target is to update
 * steps with category but no exerciseName, for these steps, we'll update the
 * exerciseName to the same value of category
 * @param {WorkoutDTO} workoutData
 * @returns {WorkoutDTO}
 */
export const updateWorkoutExerciseName = (workoutData) => {
    /**
     * Internal recurvise function to update step exerciseName
     * @param {WorkoutStepDTO[]} steps
     */
    const updateStepsExerciseName = (steps) => {
        return steps.map((step) => {
            if (step.type === RepeatGroupDTOType) {
                return {
                    ...step,
                    workoutSteps: updateStepsExerciseName(step.workoutSteps)
                };
            } else {
                return {
                    ...step,
                    exerciseName: step.exerciseName || step.category
                };
            }
        });
    };

    return {
        ...workoutData,
        workoutSegments: workoutData.workoutSegments.map((segment) => {
            return {
                ...segment,
                workoutSteps: updateStepsExerciseName(segment.workoutSteps)
            };
        })
    };
};

export const getTotalWorkoutStepsCount = (workoutSegments) => {
    if (!workoutSegments?.length) return 0;

    let totoalStepsCount = 0;
    workoutSegments.forEach((item) => {
        totoalStepsCount += getSingleWorkoutSegmentStepsCount(item);
    });
    return totoalStepsCount;
};

export const getSingleWorkoutSegmentStepsCount = (segment) => {
    let singleStepsCount = 0;
    segment.workoutSteps?.forEach((item) => {
        if (item.type === RepeatGroupDTOType) {
            singleStepsCount += getSingleWorkoutSegmentStepsCount(item);
        }
        singleStepsCount++;
    });
    return singleStepsCount;
};

// path suffix updated Workouts including Sport Specific Strength Workouts
export const mobilityPathSuffix = '_07_2024_9be99be9-75d3-4158-97d0-46e4a8a972c5';
/**
 * Get default Skip Last Rest value based on the workout structure
 * @param {string | null} [sportTypeKey]
 * @param {string | null} [lastStepType] Last step in the repeat
 * @param {string | null} [firstStepType] First step after the repeat
 * @returns {boolean | null}
 */
export const getSkipLastRestDefault = (sportTypeKey, lastStepType, firstStepType) => {
    if (lastStepType !== Types.StepTypes.Rest && lastStepType !== Types.StepTypes.Recovery) {
        return null;
    }
    switch (sportTypeKey) {
        case SportTypes.Swimming:
            if (lastStepType === Types.StepTypes.Rest && firstStepType === Types.StepTypes.Rest) {
                return true;
            }
            break;
        case SportTypes.Cycling:
            if (
                (lastStepType === Types.StepTypes.Rest && firstStepType === Types.StepTypes.Rest) ||
                (lastStepType === Types.StepTypes.Rest && firstStepType === Types.StepTypes.Cool_Down) ||
                (lastStepType === Types.StepTypes.Recovery && firstStepType === Types.StepTypes.Recovery) ||
                (lastStepType === Types.StepTypes.Recovery && firstStepType === Types.StepTypes.Cool_Down)
            ) {
                return true;
            }
            break;
        case SportTypes.Running:
        case SportTypes.Strength_Training:
        case SportTypes.Cardio:
        case SportTypes.Hiit:
        case SportTypes.Yoga:
        case SportTypes.Pilates:
        case SportTypes.Mobility:
        case SportTypes.Other:
        default:
            if (
                (lastStepType === Types.StepTypes.Rest && firstStepType === Types.StepTypes.Rest) ||
                (lastStepType === Types.StepTypes.Recovery && firstStepType === Types.StepTypes.Recovery) ||
                (lastStepType === Types.StepTypes.Recovery && firstStepType === Types.StepTypes.Cool_Down)
            ) {
                return true;
            }
            break;
    }
    return false;
};
export const containsPaceOrSendOff = (step) => {
    return (
        step.secondaryTargetType?.workoutTargetTypeKey === Types.TargetTypes.Pace_Zone ||
        step.endCondition?.conditionTypeKey === Types.ConditionTypes.Fixed_Repetition
    );
};

export const checkEmptyRepeat = (workoutSegments) => {
    return workoutSegments.some((workoutSegment) => {
        return checkEmptyRepeatForSteps(workoutSegment.workoutSteps);
    });
};

export const checkEmptyRepeatForSteps = (workoutSteps) => {
    return workoutSteps.some((step) => {
        if (step.type === RepeatGroupDTOType) {
            if (step.workoutSteps.length > 0) {
                // check child steps recursively
                return checkEmptyRepeatForSteps(step.workoutSteps);
            } else {
                // found empty repeat
                return true;
            }
        }
        return false;
    });
};

/**
 * Response of duplicate workout does not contain audio notes. Refill audio notes info from old workout.
 * @param {WorkoutDTO} oldWorkoutData
 * @param {WorkoutDTO} newWorkoutData
 * @param {(prevId: number, newId: number) => void} updateContext Move AudioData from previous step to new step
 */
export const refillAudioNotesOnDuplicate = (oldWorkoutData, newWorkoutData, updateContext) => {
    newWorkoutData.workoutAudioNote = oldWorkoutData.workoutAudioNote;

    /**
     *
     * @param {WorkoutStepDTO[]} oldWorkoutSteps
     * @param {WorkoutStepDTO[]} newWorkoutSteps
     */
    const refillSteps = (oldWorkoutSteps, newWorkoutSteps) => {
        oldWorkoutSteps.forEach((step, index) => {
            const newStep = newWorkoutSteps[index];
            if (newStep) {
                if (step.type === ExecutableStepDTOType && newStep.type === ExecutableStepDTOType) {
                    newStep.stepAudioNote = step.stepAudioNote;
                }
                if (step.type === RepeatGroupDTOType && newStep.type === RepeatGroupDTOType) {
                    refillSteps(step.workoutSteps, newStep.workoutSteps);
                }
                updateContext(step.stepId, newStep.stepId);
            }
        });
    };

    oldWorkoutData.workoutSegments.forEach((segment, index) => {
        const newSegment = newWorkoutData.workoutSegments[index];
        if (newSegment) {
            refillSteps(segment.workoutSteps, newSegment.workoutSteps);
        }
    });
};

export const areWorkoutStepsValid = (sportType, workoutConfig, workoutSteps) => {
    let isValid = true;
    workoutSteps?.forEach((stepData) => {
        if (stepData.type === RepeatGroupDTOType) {
            if (stepData.workoutSteps.length > 0) {
                // check child steps recursively
                return areWorkoutStepsValid(sportType, workoutConfig, stepData.workoutSteps);
            }
        }

        if (!isValidWorkoutStep(sportType, workoutConfig, stepData)) {
            isValid = false;
        }
    });
    return isValid;
};

export const isValidWorkoutStep = (sportType, workoutConfig, stepData) => {
    const fields = getFieldsToDisplay(sportType, stepData.stepType.stepTypeKey);

    if (
        fields.includes(StepFields.Exercises) &&
        workoutConfig?.isExerciseRequiredBasedOnStepType?.(stepData.stepType.stepTypeKey)
    ) {
        if (!stepData.exerciseName && !stepData.category) {
            return false;
        }
    }

    return true;
};
